/**
 * Staff writes the family delivery clock. The server sends at the stored
 * notifyAt and does not recompute it.
 */
import Firebase from "#classes/Firebase"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"

import {
  eventEndsWithLoadedSessions,
  familyDeliveryId,
  nextNotifyAt,
} from "./familyDelivery"

export const scheduleFamilyDelivery = async (
  studentId: string,
  classSessionId: string,
  eventEndIso: string | null | undefined,
  now: Date = new Date()
): Promise<void> => {
  if (!studentId || !classSessionId) return
  const id = familyDeliveryId(studentId, classSessionId)
  await Firebase.getInstance().mergeDocumentOrThrow(
    DOC_TYPES.FAMILY_DELIVERIES,
    id,
    {
      id,
      studentId,
      classSessionId,
      notifyAt: nextNotifyAt(eventEndIso, now),
      status: "scheduled",
      sentAt: null,
    }
  )
}

/**
 * One clock per student and class session, even when several notes are
 * approved together. A session id missing from the subscription map is
 * read here. No clock is written until every required session end is
 * known. Starting some and then throwing would toast a total failure
 * after clocks already existed, and a retry would reset those clocks.
 * `nextNotifyAt` with a missing end uses now + 10 minutes and can send
 * during class.
 */
export const scheduleDeliveriesForMoments = async (
  moments: readonly {
    studentId: string
    classSessionId?: string | null
  }[],
  eventEndBySessionId: Record<string, string | null | undefined>
): Promise<void> => {
  const sessionIds = [
    ...new Set(
      moments.flatMap((moment) =>
        moment.classSessionId ? [moment.classSessionId] : []
      )
    ),
  ]
  const missing = sessionIds.filter((id) => !(id in eventEndBySessionId))
  const loaded =
    missing.length === 0
      ? []
      : await Firebase.getInstance().getDocumentsByIds<ClassSessionConstructor>(
          DOC_TYPES.CLASS_SESSIONS,
          missing
        )
  const eventEnds = eventEndsWithLoadedSessions(
    eventEndBySessionId,
    sessionIds,
    loaded
  )
  const unresolved = sessionIds.filter((id) => !(id in eventEnds))
  if (unresolved.length > 0) {
    throw new Error("Class session end is not loaded")
  }
  const seen = new Set<string>()
  for (const moment of moments) {
    if (!moment.studentId || !moment.classSessionId) continue
    const id = familyDeliveryId(moment.studentId, moment.classSessionId)
    if (seen.has(id)) continue
    seen.add(id)
    await scheduleFamilyDelivery(
      moment.studentId,
      moment.classSessionId,
      eventEnds[moment.classSessionId]
    )
  }
}

export const cancelFamilyDelivery = async (
  studentId: string,
  classSessionId: string
): Promise<void> => {
  if (!studentId || !classSessionId) return
  const id = familyDeliveryId(studentId, classSessionId)
  await Firebase.getInstance().mergeDocumentOrThrow(
    DOC_TYPES.FAMILY_DELIVERIES,
    id,
    {
      id,
      studentId,
      classSessionId,
      status: "cancelled",
    }
  )
}
